import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { DashboardItem } from '../../api/app-types/dashboard.app';
import { PERSON_TYPE } from '../../api/app-types/search.app';
import { LvrPaddingDirective } from '../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { hasPermission } from '../../shared/permissions/permission.func';
import { SessionManagerService } from '../../shared/storage/session-manager.service';
import { TENANT_FEATURES } from '../../core/services/tenant-feature.service';
import { ClientView } from '../client/client-view/client-view';
import { NetworkContactOverview } from '../network-contact/network-contact-overview/network-contact-overview';
import { SpecialistsOverview } from '../user/specialists-overview/specialists-overview';
import { REPRESENTATION_MODE } from '../../core/services/representation-mode.service';

@Component({
	selector: 'lvr-dashboard',
	imports: [ClientView, LvrPaddingDirective, NetworkContactOverview, SpecialistsOverview],
	templateUrl: './dashboard.html',
	styleUrl: './dashboard.scss',
})
export class Dashboard {
	private readonly session = inject(SessionManagerService);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	private readonly representationMode = inject(REPRESENTATION_MODE);
	private readonly data = toSignal(inject(ActivatedRoute).data);

	public readonly clientPage = computed(() => this.data()?.['clients'] ?? []);
	public readonly institutionPage = computed(() => this.data()?.['institutions'] ?? []);
	public readonly itemsPage = computed<PaginatedResponse<DashboardItem>>(() => this.data()?.['items'] ?? []);

	public readonly dashboardClientsReadPermission = hasPermission(this.session.permissions, 'Dashboard.Clients.Read');
	public readonly hasClientsReadPermission = hasPermission(this.session.permissions, 'Clients.Read');
	public readonly hasClientsReadPhaseOneOnlyPermission = hasPermission(
		this.session.permissions,
		'Clients.Read.PhaseOneOnly',
	);
	public readonly specialistRead = hasPermission(this.session.permissions, 'Dashboard.Fachkraefte.Read');
	public readonly hasContactsReadPermission = hasPermission(this.session.permissions, 'Contacts.Read');
	public readonly hasContactsReadPhaseOneOnlyPermission = hasPermission(
		this.session.permissions,
		'Contacts.Read.PhaseOneOnly',
	);

	public readonly isRepresentationActive = this.representationMode.isActive;

	public readonly canReadClients = computed(() => {
		const hasDashboardAccess = this.dashboardClientsReadPermission();
		const hasFullRead = this.hasClientsReadPermission();
		const hasPhaseOneOnly = this.hasClientsReadPhaseOneOnlyPermission();
		const isRepresentationActive = this.isRepresentationActive();

		return hasDashboardAccess && (hasFullRead || (hasPhaseOneOnly && isRepresentationActive));
	});

	public readonly canReadContacts = computed(() => {
		return (
			this.hasContactsReadPermission() ||
			(this.hasContactsReadPhaseOneOnlyPermission() && this.isRepresentationActive())
		);
	});

	public readonly canShowNetworkContactOverview = computed(
		() => this.tenantFeatures.dashboardNetworkContactOverviewVisible() && this.canReadContacts(),
	);

	public isUserPage() {
		return this.itemsPage().entities.some((i) => i.personType === PERSON_TYPE.USER);
	}
}
